// Configuration API
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://events.logfit.eu/api/v1';

// Service de scoring en ligne (page Jour J). Sans variable, on le déduit de
// l'API : events.logfit.eu → scoring.logfit.eu, events-staging → scoring-staging.
const deriveScoringUrl = (apiUrl: string): string => {
  try {
    const url = new URL(apiUrl);
    if (url.hostname.startsWith('events')) {
      url.hostname = url.hostname.replace(/^events/, 'scoring');
      return url.toString().replace(/\/$/, '');
    }
  } catch { /* URL invalide : on garde la valeur par défaut */ }
  return 'https://scoring.logfit.eu/api/v1';
};
export const SCORING_API_URL = process.env.REACT_APP_SCORING_API_URL || deriveScoringUrl(API_BASE_URL);

// Environnement (development, staging, production)
export const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'production';

// URLs des sites
export const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || 'https://logfit.eu';

// Définition de tous les thèmes disponibles
export const THEMES = {
  logfit: {
    name: 'LogFit Original',
    mode: 'dark',
    primary: '#ffb300',
    background: '#0a0a0a',
    secondary: '#1a1a1a',
    text: '#FFFFFF',
    textSecondary: '#a0a0a0',
    border: '#2a2a2a',
    accent: '#ffd54f',
    error: '#ef4444',
    success: '#10b981',
    warning: '#f59e0b',
    info: '#3b82f6'
  },
  logfitGreen: {
    name: 'LogFit Green',
    mode: 'dark',
    primary: '#97FB57',
    background: '#121212',
    secondary: '#1E1E1E',
    text: '#FFFFFF',
    textSecondary: '#909090',
    border: '#2A2A2A',
    accent: '#7FE038',
    error: '#FF5252',
    success: '#4CAF50',
    warning: '#FFA726',
    info: '#42A5F5'
  },
  professional: {
    name: 'Professional Blue',
    mode: 'dark',
    primary: '#00D9FF',
    background: '#121212',
    secondary: '#1E1E1E',
    text: '#FFFFFF',
    textSecondary: '#909090',
    border: '#2A2A2A',
    accent: '#0EA5E9',
    error: '#FF5252',
    success: '#4CAF50',
    warning: '#FFA726',
    info: '#42A5F5'
  },
  lightBlue: {
    name: 'Light Blue',
    mode: 'light',
    primary: '#0EA5E9',
    background: '#FFFFFF',
    secondary: '#F5F5F5',
    text: '#1E1E1E',
    textSecondary: '#616161',
    border: '#E0E0E0',
    accent: '#0288D1',
    error: '#D32F2F',
    success: '#388E3C',
    warning: '#F57C00',
    info: '#1976D2'
  },
  reebok: {
    name: 'Reebok Style',
    mode: 'light',
    primary: '#0EA5E9',
    background: '#FFFFFF',
    secondary: '#F5F5F5',
    text: '#1E1E1E',
    textSecondary: '#616161',
    border: '#E0E0E0',
    accent: '#0288D1',
    accentSecondary: '#D32F2F',
    error: '#D32F2F',
    success: '#388E3C',
    warning: '#F57C00',
    info: '#1976D2'
  },
  sunset: {
    name: 'Sunset',
    mode: 'dark',
    primary: '#FF8C42',
    background: '#121212',
    secondary: '#1E1E1E',
    text: '#FFFFFF',
    textSecondary: '#909090',
    border: '#2A2A2A',
    accent: '#FF6B35',
    error: '#FF5252',
    success: '#4CAF50',
    warning: '#FFA726',
    info: '#42A5F5'
  },
  deepPurple: {
    name: 'Deep Blue Night',
    mode: 'dark',
    primary: '#ffb300',
    background: '#0a0a0a',
    secondary: '#1a1a1a',
    text: '#FFFFFF',
    textSecondary: '#a0a0a0',
    border: '#2a2a2a',
    accent: '#ffd54f',
    error: '#ef4444',
    success: '#10b981',
    warning: '#f59e0b',
    info: '#3b82f6'
  }
} as const;

// Type pour les clés de thèmes
export type ThemeKey = keyof typeof THEMES;

// Récupérer le thème stocké ou utiliser le défaut
const getStoredTheme = (): ThemeKey => {
  const stored = localStorage.getItem('app_theme');
  if (stored && stored in THEMES) {
    return stored as ThemeKey;
  }
  return 'deepPurple'; // Thème par défaut (Deep Purple Blue)
};

// Variable pour le thème actuel
let currentThemeKey: ThemeKey = getStoredTheme();

// Fonction pour obtenir le thème actif
export const getCurrentTheme = () => THEMES[currentThemeKey];

// Fonction pour changer de thème
export const setTheme = (themeKey: ThemeKey) => {
  if (themeKey in THEMES) {
    currentThemeKey = themeKey;
    localStorage.setItem('app_theme', themeKey);
    // Déclencher un événement personnalisé pour notifier le changement
    window.dispatchEvent(new CustomEvent('themeChange', { detail: themeKey }));
  }
};

// Fonction pour obtenir la liste des thèmes disponibles
export const getAvailableThemes = () => {
  return Object.entries(THEMES).map(([key, theme]) => ({
    key,
    name: theme.name,
    mode: theme.mode
  }));
};

// Export du thème actif pour compatibilité avec le code existant
export const THEME = getCurrentTheme();

// Configuration stockage
// Note: Les tokens sont maintenant dans des cookies HTTP-only (gérés par le backend)
// On ne stocke que les données UI nécessaires
export const STORAGE_KEYS = {
  USER: 'user_data',      // Données utilisateur pour hydratation UI
  THEME: 'app_theme'      // Préférence de thème
};
